import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgSelectOption,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { map, startWith, Subject, takeUntil } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatPseudoCheckboxModule } from '@angular/material/core';
import {
  NgOptgroupTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
  NgSelectModule,
} from '@ng-select/ng-select';

const QTR_PAYLOAD_TRACE_KEY = 'QTR_PAYLOAD_TRACE_V1';

@Component({
  selector: 'app-global-dropdown',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatPseudoCheckboxModule,
    NgSelectComponent,
    FormsModule,
    NgOptionTemplateDirective,
    NgSelectModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GlobalDropdownComponent),
      multi: true,
    },
  ],
  templateUrl: './global-dropdown.component.html',
  styleUrl: './global-dropdown.component.scss',
})
export class GlobalDropdownComponent implements ControlValueAccessor, OnChanges {
  @Input() data: any;
  @Input() controlName: string = '';

  get isYearDropdown(): boolean {
    return this.data?.name?.toLowerCase().includes('year') || this.data?.key?.toLowerCase().includes('year');
  }

  @Input() options: any[] = [];
  @Output() filterChanged = new EventEmitter<any>();
  private suppressSelectionChange = false;
  private destroy$ = new Subject<void>();

  selected: any[] = [];
  searchCtrl = new FormControl('');
  filteredOptions: any[] = [];

  selectedDropdownList: { [key: string]: any[] } = {};

  private onChangeFn: (value: any) => void = () => { };
  private onTouchedFn: () => void = () => { };

  private shouldTraceQuarterFlow(): boolean {
    return ['year', 'qtr', 'quarter', 'month'].includes(
      String(this.data?.key || '').trim().toLowerCase(),
    );
  }

  private snapshot<T>(value: T): T {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  }

  private trace(stage: string, details: any): void {
    if (!this.shouldTraceQuarterFlow()) return;
    console.log(`[${QTR_PAYLOAD_TRACE_KEY}] ${stage}`, this.snapshot({
      dropdownKey: this.data?.key,
      dropdownName: this.data?.name,
      ...details,
    }));
  }

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
  ) { }

  ngOnInit(): void {
    this.searchCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchText: string | null) => {
        const term = (searchText || '').toLowerCase();
        if (!term) {
          this.filteredOptions = [...this.options];
        } else {
          this.filteredOptions = this.options.filter((opt) => opt.toLowerCase().includes(term));
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!this.data?.apiUrl) {
      this.trace('globalDropdown:loadOptions:start', {
        apiUrl: this.data?.apiUrl,
        method: this.data?.method,
      });
      this.configService.getConfig(this.data?.apiUrl, this.data, this.data.method).subscribe({
        next: (res) => {
          this.options = res?.data || [];
          this.sortOptionsIfMonths();
          this.filteredOptions = this.options;
          this.data.optionsList = this.options;
          this.trace('globalDropdown:loadOptions:success', {
            optionCount: this.options?.length,
            sampleOptions: this.options?.slice(0, 10),
          });
        },
        error: (err) => {
          this.trace('globalDropdown:loadOptions:error', err);
          console.error(err);
        },
      });
    } else {
      this.sortOptionsIfMonths();
      this.filteredOptions = this.options || [];
      this.trace('globalDropdown:loadOptions:local', {
        optionCount: this.options?.length || 0,
        sampleOptions: this.options?.slice(0, 10) || [],
      });
    }
  }

  private sortOptionsIfMonths(): void {
    if (!this.options || this.options.length === 0) return;

    const isMonthFilter =
      this.data?.name?.toLowerCase().includes('month') ||
      this.data?.key?.toLowerCase().includes('month');

    if (isMonthFilter) {
      const monthOrder = [
        ['january', 'jan'],
        ['february', 'feb'],
        ['march', 'mar'],
        ['april', 'apr'],
        ['may'],
        ['june', 'jun'],
        ['july', 'jul'],
        ['august', 'aug'],
        ['september', 'sep'],
        ['october', 'oct'],
        ['november', 'nov'],
        ['december', 'dec']
      ];

      this.options.sort((a, b) => {
        const getIndex = (month: string) => {
          const normalized = String(month).toLowerCase().trim();
          return monthOrder.findIndex(m => m.includes(normalized));
        };
        return getIndex(a) - getIndex(b);
      });
    }
  }

  get isAllSelected(): boolean {
    return this.options?.length > 0 && this.selected?.length === this.options.length;
  }

  isItemSelected(item: any): boolean {
    return this.selected?.includes(item);
  }

  toggleSelectAll(event: any): void {
    const isChecked = event?.target?.checked;

    if (this.data?.key) {
      this.globalFilterService.activeFilter = this.data.key;
    }

    // Prevent triggering onChange again
    this.suppressSelectionChange = true;
    // this.selected = isChecked ? [...this.filteredOptions] : [];
    this.selected = isChecked ? [...this.options] : [];

    // Emit manually once
    this.onChangeFn(this.selected);
    this.onTouchedFn();

    const valueToEmit = this.data?.isMultiSelect
      ? this.selected
      : (this.selected && (!Array.isArray(this.selected) || this.selected.length > 0) ? [this.selected] : []);

    this.trace('globalDropdown:toggleSelectAll', {
      isChecked,
      selected: this.selected,
      valueToEmit,
    });

    if (this.data?.key) {
      // Pass filterListener if available
      const filterListener = this.data?.filterListener || null;
      this.globalFilterService.updateFilterSelection(
        this.data.key,
        valueToEmit,
        this.data?.isQueryParam,
        undefined,
        true,
        filterListener,
      );
    }

    this.filterChanged.emit(valueToEmit);

    setTimeout(() => {
      this.suppressSelectionChange = false;
    }, 0);
  }

  onChange(event: any): void {
    if (this.suppressSelectionChange) return;

    if (this.data?.key) {
      this.globalFilterService.activeFilter = this.data.key;
    }

    this.onChangeFn(this.selected);
    this.onTouchedFn();

    const valueToEmit = this.data?.isMultiSelect
      ? this.selected
      : (this.selected && (!Array.isArray(this.selected) || this.selected.length > 0) ? [this.selected] : []);

    this.trace('globalDropdown:onChange', {
      event,
      selected: this.selected,
      valueToEmit,
    });

    if (this.data?.key) {
      // Pass filterListener if available in data.filterListener or parent config
      const filterListener = this.data?.filterListener || null;
      this.globalFilterService.updateFilterSelection(
        this.data?.key,
        valueToEmit,
        this.data?.isQueryParam,
        undefined,
        true,
        filterListener,
      );
    }

    this.filterChanged.emit(valueToEmit);
  }

  onItemClick(event: MouseEvent, item: any): void {
    if (this.isItemSelected(item)) {
      // If item is already selected, deselect it
      event.stopPropagation();
      event.preventDefault();

      if (this.data?.isMultiSelect) {
        this.selected = (this.selected || []).filter((i: any) => i !== item);
      } else {
        this.selected = [];
      }

      this.trace('globalDropdown:onItemClick:deselect', {
        item,
        selectedAfterDeselect: this.selected,
      });

      this.onChange(null);
    }
  }

  removeSelected(item: any): void {
    this.selected = this.selected.filter((i) => i !== item);
    this.trace('globalDropdown:removeSelected', {
      item,
      selectedAfterRemove: this.selected,
    });
    this.onChange(this.selected); // emit the change
  }

  onDropdownClose() {
    this.searchCtrl.setValue('', { emitEvent: true }); // clears input
  }

  writeValue(value: any): void {
    // this.selected = value ?? (this.data?.isMultiSelect ? [] : '');
    this.selected = value || [];
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState?(isDisabled: boolean): void { }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

